const BASE = '/api/v1'

/** Nom de l'event custom déclenché quand le backend renvoie 401 sur un endpoint
 *  AUTHENTIFIÉ (i.e. pas /auth/login). Écouté par main.tsx pour invalider la
 *  query ['me'] et faire repasser App.tsx en page Login.
 *
 *  Indispensable pour les cas :
 *    - rotation du mot de passe (sessions purgées côté serveur)
 *    - expiration de la session
 *    - cookie effacé / corrompu */
export const AUTH_INVALID_EVENT = 'auth:invalid-session'

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(detail)
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    let detail = res.statusText
    try {
      const j = await res.json()
      if (j?.detail) detail = j.detail
    } catch {}
    // 401 sur un endpoint AUTRE que /auth/login → la session est invalide,
    // on signale globalement. /auth/login a son propre traitement d'erreur
    // (mauvais mot de passe) et ne doit pas déclencher de logout-event.
    if (res.status === 401 && !path.startsWith('/auth/login')) {
      try { window.dispatchEvent(new Event(AUTH_INVALID_EVENT)) } catch {}
    }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  const ct = res.headers.get('content-type') ?? ''
  return ct.includes('application/json') ? ((await res.json()) as T) : (undefined as T)
}

export const api = {
  get: <T = unknown>(p: string) => request<T>('GET', p),
  post: <T = unknown>(p: string, body?: unknown) => request<T>('POST', p, body),
  put: <T = unknown>(p: string, body?: unknown) => request<T>('PUT', p, body),
  patch: <T = unknown>(p: string, body?: unknown) => request<T>('PATCH', p, body),
  del: <T = unknown>(p: string) => request<T>('DELETE', p),
}

export async function uploadAvatar<T = unknown>(path: string, file: File): Promise<T> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  })
  if (!res.ok) {
    let detail = res.statusText
    try { const j = await res.json(); if (j?.detail) detail = j.detail } catch {}
    if (res.status === 401) {
      try { window.dispatchEvent(new Event(AUTH_INVALID_EVENT)) } catch {}
    }
    throw new ApiError(res.status, detail)
  }
  return (await res.json()) as T
}
